import { useTranslation } from "react-i18next";
import type { ChurchEvent } from "../../model/launchpad";
import { eventTimeParts } from "../../util/date";
import Card from "../ui/Card";
import { CalendarIcon } from "../ui/icons";
import EventThumbnail from "./EventThumbnail";

// Web port of UpcomingEventsCardView + UpcomingEventRow
// (EventsFeature/View/Component/EventsHomeSectionView.swift).
//
// Deliberately not built on the tile/StandardRow components: the app notes
// that its standard row renders nothing when an item has no image, which
// leaves events without artwork out of line. EventThumbnail always draws
// something, so every row keeps the same leading edge.

/** UpcomingEventRow: @ScaledMetric thumbnailEdgeLength = 60. */
const THUMBNAIL = "size-[60px]";

export interface UpcomingEventsSectionProps {
  events: ChurchEvent[];
  onEventClick?: (event: ChurchEvent) => void;
}

export default function UpcomingEventsSection(props: UpcomingEventsSectionProps) {
  const { t } = useTranslation("events");

  if (props.events.length === 0) return null;

  return <section className="flex flex-col gap-3">
    <h2 className="flex items-center gap-1 px-4 text-base font-medium text-gray-500">
      <CalendarIcon />
      {t("upcomingEvents")}
    </h2>

    <div className="px-4">
      <Card>
        <ul className="flex flex-col gap-4">
          {props.events.map((event, index) => (
            <li key={event.id} className="flex flex-col gap-4">
              {/* Divider between rows, not before the first. */}
              {index > 0 && <hr className="border-separator" />}
              <UpcomingEventRow
                event={event}
                onClick={props.onEventClick && (() => props.onEventClick?.(event))}
              />
            </li>
          ))}
        </ul>
      </Card>
    </div>
  </section>
}

function UpcomingEventRow(props: { event: ChurchEvent, onClick?: () => void }) {
  const { i18n } = useTranslation("events");
  const { attributes } = props.event;

  // Just when it starts — the full range is far too long for a compact row,
  // and the time is only worth showing when the event specifies one.
  const parts = eventTimeParts(
    attributes.startsAt,
    attributes.endsAt,
    attributes.showTimes,
    i18n.language
  );
  const { t } = useTranslation("events");
  const subtitle = parts.time
    ? t("dateAtTime", { date: parts.date, time: parts.time })
    : parts.date;

  const content = <>
    <span className={`${THUMBNAIL} shrink-0 overflow-hidden rounded-xl`}>
      <EventThumbnail url={attributes.thumbnailURL} />
    </span>

    <span className="flex min-w-0 flex-1 flex-col text-left">
      <span className="truncate text-[17px] font-semibold text-gray-900">
        {attributes.title}
      </span>
      <span className="truncate text-[15px] text-gray-500">{subtitle}</span>
    </span>

    <ChevronForwardIcon />
  </>;

  if (!props.onClick) {
    return <div className="flex items-center gap-3">{content}</div>
  }

  return <button
    type="button"
    onClick={props.onClick}
    className="flex w-full items-center gap-3 transition-opacity duration-200 hover:opacity-75"
  >
    {content}
  </button>
}

function ChevronForwardIcon() {
  return <svg viewBox="0 0 16 16" aria-hidden="true" className="size-4 shrink-0 text-accent">
    <path d="M5.5 2.5 11 8l-5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}
